import { useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { useAdminLiveInboundCampaigns } from '../api';
import { buildInboundColumns, DEFAULT_VISIBLE_COLUMNS } from '../columns';
import type { InboundCampaignRow } from '../types';

export function InboundCampaignSuperAdminPage() {
  const [campaignName, setCampaignName] = useState('');
  const [did, setDid] = useState('');
  const [appliedFilter, setAppliedFilter] = useState({ campaignName: '', did: '' });

  const query = useAdminLiveInboundCampaigns(appliedFilter);
  /*
   * Backend returns every campaign on this endpoint. Keep:
   *   - Any non-terminal row (Running / Paused / Ready / etc.)
   *   - Terminal rows whose endTime is today, so a campaign that wrapped
   *     earlier today is still visible until midnight.
   * Anything older lives on the Historical page.
   * STATUS_MAP: 2 Complete, 4/5 Error, 7 Cancelled, 11/12 System.
   */
  const TERMINAL_STATUSES = new Set([2, 4, 5, 7, 11, 12]);
  const rows = useMemo(() => {
    const todayPrefix = (() => {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    })();
    return (query.data ?? []).filter((r) => {
      const status = Number(r.campaignStatus);
      if (!TERMINAL_STATUSES.has(status)) return true;
      const stamp = r.endTime ?? r.startTime ?? '';
      return stamp.startsWith(todayPrefix);
    });
  }, [query.data]);
  const columns = useMemo(() => buildInboundColumns({ showOwner: true }), []);

  const visibility = {
    ...DEFAULT_VISIBLE_COLUMNS,
    userId: true,
    username: true,
  };

  return (
    <FillPage>
      <PageHeader
        title="All Inbound"
        description="Inbound campaigns across the users you manage. Refreshes every 30 seconds."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            aria-label="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', query.isFetching && 'animate-spin')} />
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Narrow by campaign name or by DID across all managed users.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="name">Campaign name</Label>
              <Input
                id="name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="did">DID</Label>
              <Input
                id="did"
                value={did}
                onChange={(e) => setDid(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <Button
              onClick={() =>
                setAppliedFilter({
                  campaignName: campaignName.trim(),
                  did: did.trim(),
                })
              }
            >
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<InboundCampaignRow, unknown>
        tableId="campaigns:inbound:admin:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search campaigns…"
        emptyTitle="No inbound campaigns"
        emptyDescription="When any of your users start an inbound campaign, it appears here."
        getRowId={(row) => String(row.campaignId)}
        initialColumnPinning={{ right: ['actions'] }}
        initialColumnVisibility={visibility}
        exportFilename="inbound-campaigns-admin.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
